import { configureHttp } from '@/shared/api/http'
import { queryClient } from '@/shared/api/query-client'
import { env } from '@/shared/config/env'

import { useAuthStore } from './auth-store'
import { decodeAccessToken, preflightClaims } from './claims'
import { userManager } from './oidc'

function hydrate(accessToken: string | undefined): void {
  const store = useAuthStore.getState()
  if (accessToken === undefined || accessToken === '') {
    store.setAnonymous()
    return
  }

  const claims = decodeAccessToken(accessToken)
  const problems = preflightClaims(claims, env.VITE_API_AUDIENCE, env.VITE_KEYCLOAK_CLIENT_ID)

  // Falha ANTES do primeiro request: com aud/tenant_id errados, toda chamada
  // daria 401 `invalid_token` — indistinguível de token corrompido.
  if (problems.length > 0 || !claims?.tenant_id || !claims.sub) {
    store.setMisconfigured(problems)
    return
  }

  store.setAuthenticated({
    subject: claims.sub,
    tenantId: claims.tenant_id,
    roles: claims.realm_access?.roles ?? [],
    email: claims.email ?? null,
    name: claims.name ?? null,
  })
}

/**
 * Roda UMA vez, antes do createRoot — assim a guarda de rota já vê o estado
 * final e não há flash de "não autenticado".
 *
 * Nunca iniciar auth dentro de useEffect no root: o duplo-invoke do StrictMode
 * rodaria duas vezes e o `code` do callback é de uso único.
 */
export async function bootstrapAuth(): Promise<void> {
  configureHttp({
    // Lê do userManager (que o automaticSilentRenew mantém fresco), nunca de uma
    // cópia no store — uma cópia ficaria velha 60s antes de expirar.
    getToken: async () => {
      const user = await userManager.getUser()
      return user && !user.expired ? user.access_token : null
    },
    onUnauthorized: () => {
      void userManager.signinRedirect({ state: { returnTo: window.location.href } })
    },
  })

  userManager.events.addUserLoaded((user) => {
    // Troca de identidade na MESMA aba (o futuro <TenantSwitcher> que o
    // topbar antecipa, ou um re-login com outro usuário sem reload): o cache do
    // TanStack Query do tenant anterior NÃO pode sobreviver. As queryKeys não
    // carregam tenant_id (ver features/*/api/keys.ts) — 1 tenant por sessão —,
    // então só o clear separa os dados de um tenant dos do outro.
    const anterior = useAuthStore.getState().subject
    hydrate(user.access_token)
    const atual = useAuthStore.getState().subject
    if (anterior !== null && anterior !== atual) {
      queryClient.clear()
    }
  })
  userManager.events.addUserUnloaded(() => {
    // Identidade removida (logout, ou o passo de remoção que precede uma troca):
    // descarta o cache do tenant ANTES de qualquer dado novo carregar. É o
    // invariante que features/*/api/keys.ts documenta e que, até a Etapa 11,
    // estava só na prosa — o handler só chamava setAnonymous().
    queryClient.clear()
    useAuthStore.getState().setAnonymous()
  })
  userManager.events.addAccessTokenExpired(() => {
    void userManager.signinRedirect()
  })
  userManager.events.addSilentRenewError(() => {
    void userManager.signinRedirect()
  })

  // A rota /auth/callback processa o `code` — não interferir aqui.
  if (window.location.pathname === env.VITE_KEYCLOAK_REDIRECT_PATH) return

  const user = await userManager.getUser()
  if (user && !user.expired) {
    hydrate(user.access_token)
    return
  }
  if (user?.refresh_token) {
    try {
      const renovado = await userManager.signinSilent()
      hydrate(renovado?.access_token)
      return
    } catch {
      // refresh vencido/revogado → anônimo; a guarda de rota manda para o login.
    }
  }
  useAuthStore.getState().setAnonymous()
}
